import React, { useMemo } from "react";
import type { All8PageContent } from "@bcc/core-renderers";
import { LoadingSpinner } from "../LoadingSpinner";
import PageConnectionsModal from "../PageConnectionsModal";
import PageEditorPreviewPanel, { type PreviewViewportKey } from "./PageEditorPreviewPanel";
import PageEditorMetadataPanel from "./PageEditorMetadataPanel";
import PageEditorLayoutPanel from "./PageEditorLayoutPanel";
import PageEditorComponentsPanel from "./PageEditorComponentsPanel";
import ContentLinksPanel from "../ContentLinksPanel";
import ContentHistoryPanel from "../ContentHistory/ContentHistoryPanel";
import PageEditorJsonPanel from "./PageEditorJsonPanel";
import PageEditorMarkdownPanel from "./PageEditorMarkdownPanel";
import PageEditorComponentLibraryModal from "./PageEditorComponentLibraryModal";
import type { ComponentLibraryPicker } from "./useComponentLibraryPicker";
import type { ComponentHistoryEntry } from "./useComponentHistory";
import PageEditorViewControls from "./PageEditorViewControls";
import EditorActionBar from "../Content/EditorActionBar";
import PageTreePanel from "../PageTree/PageTreePanel";
import type { ContentPublishStatus } from "../ContentStatusPill";
import type { AdminPage } from "./types";
import type { ComponentDefinition } from "../../Pages/pageComponentLibrary";
import type { BCCADMINBaseModelsPageTreeNode } from "../../API/Models";

export type ContentViewMode = "blocks" | "json" | "markdown";

export type PageEditorLayoutProps = {
	editable: boolean;
	error: string | null;
	message: string | null;
	/** Shown when the draft has unsaved edits */
	unsavedChangesMessage?: string | null;
	/** Shown when save is disabled (e.g. "Slug is missing.") */
	saveBlockerReasons?: string[];
	/** Shown when saved changes are not yet deployed to preview or production */
	pendingDeployMessage?: string | null;
	loadingView: boolean;
	draft: AdminPage | null;
	contentViewMode: ContentViewMode;
	isJsonLocked: boolean;
	pageJsonError: string | null;
	onSelectBlocks: () => void;
	onSelectJson: () => void;
	onSelectMarkdown: () => void;
	/** Markdown source when view is markdown; edits round-trip to draft. Invalid content shows error but does not lock editing. */
	markdownDraft: string;
	markdownError: string | null;
	onMarkdownChange: (value: string) => void;
	onShowConnections: () => void;
	/** Ouvre l'analyse « par ou arrive-t-on ici depuis l'accueil ? ». */
	onShowReachability?: () => void;
	connectionsDisabled: boolean;
	metaOpen: boolean;
	setMetaOpen: React.Dispatch<React.SetStateAction<boolean>>;
	setDraft: React.Dispatch<React.SetStateAction<AdminPage | null>>;
	resolvedPath: string;
	canChangeSlug?: boolean;
	onOpenSlugChange?: () => void;
	dynamicPreviewLabel?: string | null;
	onOpenDynamicPreview?: () => void;
	onClearDynamicPreview?: () => void;
	allowImageVariables?: boolean;
	previewViewport: PreviewViewportKey;
	setPreviewViewport: (viewport: PreviewViewportKey) => void;
	/** URL to open in new tab for preview site (empty = hide button) */
	previewPageUrl: string;
	/** URL to open in new tab for production site (empty = hide button) */
	productionPageUrl: string;
	previewContainerRef: React.RefObject<HTMLDivElement>;
	previewCss: string;
	editorComponents: All8PageContent;
	previewComponents: All8PageContent;
	previewWidth: number | null;
	previewContainerWidth: number | null;
	runtimeConfig: any;
	activeComponentKey: string | null;
	setActiveComponentKey: React.Dispatch<React.SetStateAction<string | null>>;
	updateComponentValue: (index: number, path: Array<string | number>, value: any) => void;
	removeComponent: (index: number) => void;
	moveComponent: (from: number, to: number) => void;
	/** Inserts a deep copy of the component right below the original (blocks view). */
	duplicateComponent?: (index: number) => void;
	/** Wraps the component at `index` into a new Container (blocks view). */
	onWrapInContainer?: (index: number) => void;
	/** Lifts a Container's children back out and removes it (blocks view). */
	onUnwrapContainer?: (index: number) => void;
	/** Session undo/redo for the component list (blocks view toolbar). */
	onUndo?: () => void;
	onRedo?: () => void;
	canUndo?: boolean;
	canRedo?: boolean;
	/** Session history timeline for the « Historique » modal. */
	historyEntries?: ComponentHistoryEntry[];
	historyIndex?: number;
	onJumpHistory?: (index: number) => void;
	/** Real deployed dev/prod paths for this page (from /api/content/deployment-links). */
	deployLinks?: { preview: string[]; production: string[] } | null;
	/** Inline version-history panel (same component the custom-content editor renders), placed in the
	 *  settings column instead of a floating button. */
	versionsPanel?: React.ReactNode;
	/** Opens the component library targeting a gap index between cards (blocks view). */
	onInsertComponentAt?: (gapIndex: number) => void;
	/** Index of the card currently open in the components panel (preview highlight). */
	activeComponentIndex?: number | null;
	/** Selects the card matching a block clicked inside the preview iframe. */
	onSelectComponentFromPreview?: (index: number) => void;
	showLibrary: boolean;
	setShowLibrary: React.Dispatch<React.SetStateAction<boolean>>;
	libraryPicker: ComponentLibraryPicker;
	componentDefinitionMap: Map<string, ComponentDefinition>;
	addComponentFromDefinition: (definition: ComponentDefinition | null) => void;
	/** When in markdown view, modal selection inserts at cursor via this ref. */
	markdownInsertRef: React.MutableRefObject<((def: ComponentDefinition) => void) | null>;
	pageJsonDraft: string;
	handleFormatJson: () => void;
	handlePageJsonChange: (value: string) => void;
	jsonEditorOptions: any;
	/** Simplified publication state shown on the action bar's pill. */
	publishStatus: ContentPublishStatus;
	/** Stable content id for « Publier ». */
	publishContentId?: string | null;
	publishDisabled?: boolean;
	/** Hide « Publier » while the page has unsaved edits. */
	publishHidden?: boolean;
	onPublished: () => void;
	onPublishingChange: (publishing: boolean) => void;
	/* --- Secondary bar (shared with the content-data editor via EditorActionBar) --- */
	onAnalyze?: () => void;
	/** Route to the translation workspace for this page. */
	translateHref?: string;
	onCreateTicket?: () => void;
	onSelectPageFromTree: (node: BCCADMINBaseModelsPageTreeNode) => void;
	connectionsOpen: boolean;
	setConnectionsOpen: React.Dispatch<React.SetStateAction<boolean>>;
	initialLanguage: string;
	/** Page tree (left nav) visible; toggle to hide/show. */
	pageNavOpen: boolean;
	setPageNavOpen: React.Dispatch<React.SetStateAction<boolean>>;
	/** Page is soft-deleted; show red banner and Restore button. */
	isPageDeleted?: boolean;
	onRestore?: () => void | Promise<void>;
	restoring?: boolean;
	/** Extra content rendered at the very top of the banner stack (linked tickets, authorship banner). */
	topBannerSlot?: React.ReactNode;
};

/** Main page editor layout with navigation tree, editing panel and preview. */
const PageEditorLayout: React.FC<PageEditorLayoutProps> = ({
	editable,
	error,
	message,
	unsavedChangesMessage,
	saveBlockerReasons = [],
	pendingDeployMessage,
	loadingView,
	draft,
	contentViewMode,
	isJsonLocked,
	pageJsonError,
	onSelectBlocks,
	onSelectJson,
	onSelectMarkdown,
	markdownDraft,
	markdownError,
	onMarkdownChange,
	onShowConnections,
	onShowReachability,
	connectionsDisabled,
	metaOpen,
	versionsPanel,
	setMetaOpen,
	setDraft,
	resolvedPath,
	canChangeSlug,
	onOpenSlugChange,
	dynamicPreviewLabel,
	onOpenDynamicPreview,
	onClearDynamicPreview,
	allowImageVariables,
	previewViewport,
	setPreviewViewport,
	previewPageUrl,
	productionPageUrl,
	previewContainerRef,
	previewCss,
	editorComponents,
	previewComponents,
	previewWidth,
	previewContainerWidth,
	runtimeConfig,
	activeComponentKey,
	setActiveComponentKey,
	updateComponentValue,
	removeComponent,
	moveComponent,
	duplicateComponent,
	onWrapInContainer,
	onUnwrapContainer,
	onUndo,
	onRedo,
	canUndo,
	canRedo,
	historyEntries,
	historyIndex,
	onJumpHistory,
	deployLinks,
	onInsertComponentAt,
	activeComponentIndex,
	onSelectComponentFromPreview,
	showLibrary,
	setShowLibrary,
	libraryPicker,
	componentDefinitionMap,
	addComponentFromDefinition,
	markdownInsertRef,
	pageJsonDraft,
	handleFormatJson,
	handlePageJsonChange,
	jsonEditorOptions,
	publishStatus,
	publishContentId,
	publishDisabled,
	publishHidden,
	onPublished,
	onPublishingChange,
	onAnalyze,
	translateHref,
	onCreateTicket,
	onSelectPageFromTree,
	connectionsOpen,
	setConnectionsOpen,
	initialLanguage,
	pageNavOpen,
	setPageNavOpen,
	isPageDeleted,
	onRestore,
	restoring,
	topBannerSlot
}) => {
	const normalizedSlug = useMemo(() => (draft?.slug ?? "").trim().replace(/^\/+|\/+$/g, ""), [draft?.slug]);
	const expandedPaths = useMemo(() => {
		if (!normalizedSlug) return [];
		const segments = normalizedSlug.split("/").filter(Boolean);
		return segments.slice(0, -1).map((_, index) => segments.slice(0, index + 1).join("/"));
	}, [normalizedSlug]);

	// Every banner : « dernier éditeur », pending-publication, unsaved, errors : belongs to the page's
	// content, not to its chrome. They used to be pinned above the editor, permanently eating the top of
	// the screen; they now ride inside the scrolling column with everything else.
	const banners = (
		<div className="shrink-0 space-y-3">
			{topBannerSlot}
			{isPageDeleted && (
					<div
						className="flex flex-wrap items-center justify-between gap-3 rounded-xl border-2 border-rose-300 bg-rose-100 px-4 py-3 text-sm font-medium text-rose-800"
						role="alert"
					>
						<span>This page is deleted. Restore it to publish to preview again.</span>
						{onRestore && (
							<button
								type="button"
								onClick={() => onRestore()}
								disabled={restoring}
								className="inline-flex items-center gap-2 rounded-lg border border-rose-300 bg-rose-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-rose-700 disabled:opacity-60"
							>
								{restoring ? (
									<>
										<span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-white border-t-transparent" />
										Restoring…
									</>
								) : (
									<>Restore page</>
								)}
							</button>
						)}
					</div>
				)}
				{unsavedChangesMessage && (
					<div
						className="flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm font-medium text-amber-800"
						role="status"
					>
						<span className="inline-flex h-2 w-2 shrink-0 rounded-full bg-amber-500" aria-hidden />
						{unsavedChangesMessage}
					</div>
				)}
				{saveBlockerReasons.length > 0 && (
					<div
						className="flex flex-col gap-1 rounded-xl border border-rose-200 bg-rose-50 px-4 py-2.5 text-sm font-medium text-rose-700"
						role="alert"
					>
						{saveBlockerReasons.map((reason) => (
							<span key={reason}>{reason}</span>
						))}
					</div>
				)}
				{pendingDeployMessage && (
					<div
						className="flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm font-medium text-amber-800"
						role="status"
					>
						<span className="inline-flex h-2 w-2 shrink-0 rounded-full bg-amber-500" aria-hidden />
						{pendingDeployMessage}
					</div>
				)}
				{error && (
					<div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-2 text-sm text-rose-700">
						{error}
					</div>
				)}
			{message && (
				<div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm text-emerald-700">
					{message}
				</div>
			)}
		</div>
	);

	return (
		<div className="w-full h-full flex flex-col overflow-hidden">
			{/* The secondary bar, directly under the app's top bar and identical to the content-data
			    editor's : see Components/Content/EditorActionBar. The commit actions are NOT here: they
			    live in the top bar itself (EditorCommitActions, wired from Pages/PageEditor.tsx). */}
			<EditorActionBar
				leadingSlot={
					<PageEditorViewControls
						contentViewMode={contentViewMode}
						isJsonLocked={isJsonLocked}
						pageJsonError={pageJsonError}
						onSelectBlocks={onSelectBlocks}
						onSelectJson={onSelectJson}
						onSelectMarkdown={onSelectMarkdown}
						pageNavOpen={pageNavOpen}
						onTogglePageNav={() => setPageNavOpen((open) => !open)}
					/>
				}
				status={publishStatus}
				previewUrls={deployLinks?.preview}
				productionUrls={deployLinks?.production}
				onShowConnections={onShowConnections}
				connectionsDisabled={connectionsDisabled}
				isEditing={editable}
				onAnalyze={onAnalyze}
				analyzeDisabled={loadingView}
				translateHref={translateHref}
				onCreateTicket={onCreateTicket}
				onShowReachability={onShowReachability}
			/>

			<div className="flex-1 min-h-0 px-4 sm:px-6 pb-6 pt-4">
				{loadingView && !draft ? (
					<div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-12 text-center text-slate-500">
						<LoadingSpinner size="lg" />
						<span className="ml-2 inline-block align-middle">Loading page…</span>
					</div>
				) : (
					<div className="flex h-full gap-5">
						{pageNavOpen && (
							<PageTreePanel
								className="w-[350px] shrink-0 h-full"
								language={draft?.language ?? initialLanguage}
								activePageId={draft?.id ?? null}
								activePageSlug={draft?.slug ?? null}
								defaultExpandedPaths={expandedPaths}
								onSelectPage={onSelectPageFromTree}
							/>
						)}
						{/* The editor column is the ONE scroll container: the blocks, and the info panels
						    stacked under them, scroll together. It used to be the left grid cell that
						    scrolled, which is why « Liens de la page » and « Versions » were stuck
						    half-width inside it and the preview faced the whole settings stack rather
						    than the blocks. */}
						<div className="flex-1 min-w-0 h-full flex flex-col gap-4 overflow-y-auto pr-1">
							{banners}
							{/* Metadata (slug, SEO) spans the full width ABOVE the split, collapsed by default:
							    it is page-wide routing information, not a block. Keeping it out of the left cell
							    is what lets « Aperçu en direct » start level with « Layout ». */}
							{contentViewMode === "blocks" && (
								<PageEditorMetadataPanel
									editable={editable}
									draft={draft}
									metaOpen={metaOpen}
									onToggleMeta={() => setMetaOpen((open) => !open)}
									setDraft={setDraft}
									resolvedPath={resolvedPath}
									canChangeSlug={canChangeSlug}
									onOpenSlugChange={onOpenSlugChange}
								/>
							)}
							<div className="grid shrink-0 gap-5 md:grid-cols-2">
								<div className="min-w-0">
									{contentViewMode === "blocks" ? (
										<div className="space-y-4">
											<PageEditorLayoutPanel
												editable={editable}
												value={draft?.layout ?? null}
												onChange={(next) =>
													setDraft((prev) => (prev ? { ...prev, layout: next } : prev))
												}
											/>
											<PageEditorComponentsPanel
												editable={editable}
												previewComponents={editorComponents}
												componentDefinitionMap={componentDefinitionMap}
												activeComponentKey={activeComponentKey}
												setActiveComponentKey={setActiveComponentKey}
												updateComponentValue={updateComponentValue}
												removeComponent={removeComponent}
												moveComponent={moveComponent}
												duplicateComponent={duplicateComponent}
												onWrapInContainer={onWrapInContainer}
												onUnwrapContainer={onUnwrapContainer}
												onUndo={onUndo}
												onRedo={onRedo}
												canUndo={canUndo}
												canRedo={canRedo}
												historyEntries={historyEntries}
												historyIndex={historyIndex}
												onJumpHistory={onJumpHistory}
												onInsertAt={onInsertComponentAt}
												onOpenLibrary={() => setShowLibrary(true)}
												onAddPageHeader={() => addComponentFromDefinition(componentDefinitionMap.get("PageHeader") ?? null)}
												allowImageVariables={allowImageVariables}
											/>
										</div>
									) : contentViewMode === "markdown" ? (
										<PageEditorMarkdownPanel
											editable={editable}
											value={markdownDraft}
											error={markdownError}
											onChange={onMarkdownChange}
											editorOptions={jsonEditorOptions}
											componentDefinitionMap={componentDefinitionMap}
											pageTitle={(() => {
												const t = (draft?.title ?? "").trim();
												if (t) return t;
												const first = draft?.components?.[0];
												if (first?.content != null && typeof first.content === "object") {
													const ct = String((first.content as { title?: string }).title ?? "").trim();
													if (ct) return ct;
												}
												return (draft?.slug ?? "").trim() || "";
											})()}
											pageDescription={draft?.metadata?.description ?? undefined}
											onOpenComponentLibrary={() => setShowLibrary(true)}
											insertComponentRef={markdownInsertRef}
										/>
									) : (
										<PageEditorJsonPanel
											editable={editable}
											pageJsonDraft={pageJsonDraft}
											pageJsonError={pageJsonError}
											onFormat={handleFormatJson}
											onChange={handlePageJsonChange}
											jsonEditorOptions={jsonEditorOptions}
										/>
									)}
								</div>

								{/* Sticky + self-start: the preview keeps facing the blocks you are editing as the
								    column scrolls, instead of stretching down the whole settings stack. Below
								    md the grid is a single column, so it just flows under the editor. top-0: the
								    action bar now sits OUTSIDE this scroll container, so nothing overlaps it. */}
								<div className="min-w-0 md:sticky md:top-0 md:self-start md:h-[calc(100vh-12rem)] md:min-h-[560px]">
									<PageEditorPreviewPanel
										previewViewport={previewViewport}
										onViewportChange={setPreviewViewport}
										pageId={draft?.id ?? null}
										pageTitle={draft?.title ?? draft?.slug ?? null}
										previewPageUrl={previewPageUrl}
										productionPageUrl={productionPageUrl}
										previewContainerRef={previewContainerRef}
										previewCss={previewCss}
										previewComponents={previewComponents}
										previewWidth={previewWidth}
										previewContainerWidth={previewContainerWidth}
										runtimeConfig={runtimeConfig}
										dynamicPreviewLabel={dynamicPreviewLabel}
										onOpenDynamicPreview={onOpenDynamicPreview}
										onClearDynamicPreview={onClearDynamicPreview}
										activeComponentIndex={contentViewMode === "blocks" ? activeComponentIndex : null}
										onSelectComponent={contentViewMode === "blocks" ? onSelectComponentFromPreview : undefined}
										minHeight={450}
									/>
								</div>
							</div>

							{/* Page information, full width under the editor : same order and same card chrome
							    as the content editor: where the page lives, then its versions, then who did
							    what to it. */}
							{contentViewMode === "blocks" && (
								<div className="shrink-0 space-y-4 pb-2">
									<ContentLinksPanel
										previewUrls={deployLinks?.preview}
										productionUrls={deployLinks?.production}
										fallbackPath={draft?.slug ?? null}
									/>
									{versionsPanel}
									{draft?.id && <ContentHistoryPanel contentId={draft.id} />}
								</div>
							)}
						</div>
					</div>
				)}
			</div>

		<PageEditorComponentLibraryModal
			show={showLibrary}
			onClose={() => {
				setShowLibrary(false);
				libraryPicker.reset();
			}}
			picker={libraryPicker}
			onAdd={addComponentFromDefinition}
			runtimeConfig={runtimeConfig}
		/>

		<PageConnectionsModal
			open={connectionsOpen}
			onClose={() => setConnectionsOpen(false)}
			pageId={draft?.id ?? null}
			slug={draft?.slug ?? null}
			language={draft?.language ?? initialLanguage}
		/>
		</div>
	);
};

export default PageEditorLayout;
